import React, { useState, useEffect } from "react";
import { useAuth } from "./contexts/AuthContext";
import {
  listUsers,
  grantUserOrgAccess,
  ensureUserFederatedId,
} from "./utils/userManagementApi";
import logger from "./utils/logger";
import "./UserSelectorModal.css";

const UserSelectorModal = ({
  isOpen,
  onClose,
  organization,
  authorizedUserIds,
  onUserGranted,
}) => {
  const { getAccessToken } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [message, setMessage] = useState(null);

  // Confirmation state
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [granting, setGranting] = useState(false);
  const [grantError, setGrantError] = useState(null);

  const limit = 20;

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    } else {
      // Clear search term when modal closes
      setSearchTerm("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, searchTerm, roleFilter, page]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && !granting) {
        if (showConfirm) {
          setShowConfirm(false);
          setSelectedUser(null);
          setGrantError(null);
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleEsc);
      return () => window.removeEventListener("keydown", handleEsc);
    }
  }, [isOpen, showConfirm, granting, onClose]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      // Don't clear success messages, only error messages
      setMessage((prev) => (prev?.type === "success" ? prev : null));

      const params = {
        page,
        limit,
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter && { role: roleFilter }),
      };

      logger.log("[UserSelectorModal] Loading users with params:", params);
      const result = await listUsers(params, getAccessToken);

      setUsers(result.users || []);
      setTotalPages(result.pagination?.totalPages || 1);
      setTotalUsers(result.pagination?.total || 0);

      logger.log(
        `[UserSelectorModal] Loaded ${result.users?.length} users, page ${page} of ${totalPages}`
      );
    } catch (error) {
      logger.error("[UserSelectorModal] Failed to load users:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to load users",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUserClick = (user) => {
    // Don't allow selection of unusable accounts
    if (user.unusableAccount) {
      return;
    }

    // Check if user is already authorized
    const isAuthorized = authorizedUserIds.has(user.userId);
    if (isAuthorized) {
      return; // User is already authorized, don't allow selection
    }

    setSelectedUser(user);
    setShowConfirm(true);
    setGrantError(null);
  };

  const handleGrantAccess = async () => {
    if (!selectedUser) return;

    try {
      setGranting(true);
      setGrantError(null);

      logger.log(
        "[UserSelectorModal] Granting access to user:",
        selectedUser.userId,
        "for org:",
        organization.orgId
      );

      // Check/set federated ID first
      const federatedIdResult = await ensureUserFederatedId(
        selectedUser.userId,
        getAccessToken
      );
      logger.log(
        "[UserSelectorModal] Federated ID check result:",
        federatedIdResult
      );

      // Grant org access
      await grantUserOrgAccess(
        selectedUser.userId,
        organization.orgId,
        getAccessToken
      );

      // Clear cache
      localStorage.removeItem("orgs_cache");
      localStorage.removeItem("orgs_cache_timestamp");
      logger.log("[UserSelectorModal] Cleared localStorage cache after grant");

      // Show success message
      setMessage({
        type: "success",
        text: `Successfully granted ${selectedUser.firstName} ${selectedUser.lastName} access to ${organization.name}`,
      });

      // Add to authorized list locally (optimistic update)
      authorizedUserIds.add(selectedUser.userId);

      // Close confirmation and reset
      setShowConfirm(false);
      setSelectedUser(null);

      // Clear search bar immediately
      setSearchTerm("");

      // Auto-dismiss success message after 8 seconds
      setTimeout(() => {
        setMessage(null);
      }, 8000);

      // Notify parent to refresh
      if (onUserGranted) {
        onUserGranted();
      }
    } catch (error) {
      logger.error("[UserSelectorModal] Failed to grant access:", error);

      // Check for specific error cases
      if (
        error.message?.includes("federation configuration mismatch") ||
        error.message?.includes("federationMismatch")
      ) {
        setGrantError(
          `❌ This account cannot be used with this application due to a federation configuration mismatch. Please delete this account and create a new one.`
        );
        // Refresh user list to show the unusable flag
        setTimeout(() => {
          loadUsers();
        }, 1000);
      } else if (
        error.message?.includes("User does not exist") ||
        error.message?.includes("user does not exist")
      ) {
        setGrantError(
          `❌ ${selectedUser.firstName} ${selectedUser.lastName} has not logged into the system yet. They must log in at least once before organization access can be granted.`
        );
      } else if (
        error.message?.includes("already assigned") ||
        error.message?.includes("already has access")
      ) {
        setGrantError(
          `${selectedUser.firstName} ${selectedUser.lastName} already has access to ${organization.name}. Refreshing...`
        );
        // Auto-close and refresh
        setTimeout(() => {
          setShowConfirm(false);
          setSelectedUser(null);
          setGrantError(null);
          if (onUserGranted) {
            onUserGranted();
          }
        }, 2000);
      } else if (
        error.message?.includes("log in at least once") ||
        error.message?.includes("federated user ID") ||
        error.message?.includes("User does not exist")
      ) {
        setGrantError(
          `${selectedUser.firstName} ${selectedUser.lastName} must log in at least once before organizations can be assigned.`
        );
      } else {
        setGrantError(`Error granting access: ${error.message}`);
      }
    } finally {
      setGranting(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
    setSelectedUser(null);
    setGrantError(null);
  };

  const getUserOrgCount = (user) => {
    // Get org count from user object if available
    return user.organizationCount || user.organizations?.length || 0;
  };

  const isUserAuthorized = (user) => {
    return authorizedUserIds.has(user.userId);
  };

  // Filter users who haven't been authorized yet
  const unauthorizedUsers = users.filter((u) => !isUserAuthorized(u));
  const allUsersAuthorized =
    !loading && users.length > 0 && unauthorizedUsers.length === 0;

  if (!isOpen) return null;

  return (
    <div className="user-selector-modal-overlay" onClick={onClose}>
      <div className="user-selector-modal" onClick={(e) => e.stopPropagation()}>
        <div className="user-selector-header">
          <h3>Authorize Users for {organization.name}</h3>
          <button className="close-btn" onClick={onClose} disabled={granting}>
            ×
          </button>
        </div>

        {message && (
          <div className={`user-selector-message ${message.type}`}>
            {message.text}
          </div>
        )}

        {!showConfirm && (
          <>
            <div className="user-selector-controls">
              <input
                type="text"
                className="user-selector-search"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
              />
              <select
                className="user-selector-role-filter"
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Roles</option>
                <option value="Full Admin">Full Admin</option>
                <option value="Organization Admin">Organization Admin</option>
                <option value="Site Admin">Site Admin</option>
              </select>
            </div>

            <div className="user-selector-body">
              {loading ? (
                <div className="user-selector-loading">
                  <div className="loading-spinner"></div>
                  Loading users...
                </div>
              ) : allUsersAuthorized ? (
                <div className="user-selector-empty">
                  All users are already authorized to this organization.
                </div>
              ) : users.length === 0 ? (
                <div className="user-selector-empty">
                  {searchTerm || roleFilter
                    ? "No users match your search criteria."
                    : "No users found."}
                </div>
              ) : (
                <div className="user-selector-list">
                  {users.map((user) => {
                    const isAuthorized = isUserAuthorized(user);
                    const isUnusable = user.unusableAccount;
                    return (
                      <div
                        key={user.userId}
                        className={`user-selector-item ${
                          isAuthorized ? "authorized" : ""
                        } ${isUnusable ? "unusable-user-row" : ""}`}
                        onClick={() => handleUserClick(user)}
                        style={{
                          cursor: isUnusable ? "not-allowed" : "pointer",
                        }}
                        title={
                          isUnusable
                            ? "This account cannot be used due to a federation configuration mismatch"
                            : isAuthorized
                            ? "Already authorized"
                            : `${user.firstName} ${user.lastName}\nRole: ${
                                user.role
                              }\nOrganizations: ${getUserOrgCount(
                                user
                              )}\nEmail: ${user.email}`
                        }
                      >
                        <div className="user-selector-item-info">
                          <div
                            className="user-selector-item-name"
                            style={{
                              color: isUnusable ? "#dc3545" : "inherit",
                            }}
                          >
                            {user.firstName} {user.lastName}
                          </div>
                          <div
                            className="user-selector-item-email"
                            style={{
                              color: isUnusable ? "#dc3545" : "inherit",
                            }}
                          >
                            {user.email}
                          </div>
                          <div className="user-selector-item-role">
                            {user.role}
                            {isUnusable && (
                              <span
                                className="unusable-badge"
                                style={{ marginLeft: "8px" }}
                              >
                                UNUSABLE
                              </span>
                            )}
                          </div>
                        </div>
                        {isAuthorized && !isUnusable && (
                          <div className="user-selector-authorized-badge">
                            Already Authorized
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {!loading && users.length > 0 && (
              <div className="user-selector-pagination">
                <button
                  className="pagination-btn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {page} of {totalPages} ({totalUsers} total users)
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* Inline Confirmation */}
        {showConfirm && selectedUser && (
          <div className="user-selector-confirm">
            <h4>Grant Access</h4>
            <p>
              Are you sure you want to grant{" "}
              <strong>
                {selectedUser.firstName} {selectedUser.lastName}
              </strong>{" "}
              ({selectedUser.email}) access to{" "}
              <strong>{organization.name}</strong>?
            </p>
            <p className="user-selector-confirm-note">
              This will give them Yonomi Platform "OWNER_ADMIN" role for this
              organization.
            </p>

            {grantError && (
              <div className="user-selector-error">{grantError}</div>
            )}

            <div className="user-selector-confirm-actions">
              <button
                className="cancel-btn"
                onClick={handleCancelConfirm}
                disabled={granting}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={handleGrantAccess}
                disabled={granting}
              >
                {granting ? "Granting..." : "Confirm Grant"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSelectorModal;
